import axios from 'axios';
import { API_CONFIG } from '../config/api.config';

const trimTrailingSlash = (value = '') => value.replace(/\/+$/, '');
const trimLeadingSlash = (value = '') => value.replace(/^\/+/, '');

const resolveBaseURL = () => {
  const explicitUrl = process.env.REACT_APP_API_URL;
  if (explicitUrl && explicitUrl.trim()) {
    return `${trimTrailingSlash(explicitUrl.trim())}/`;
  }

  const fallbackBase = process.env.REACT_APP_API_BASE_URL || API_CONFIG.BASE_URL;
  const version = process.env.REACT_APP_API_VERSION || API_CONFIG.VERSION;
  const composed = `${trimTrailingSlash(fallbackBase)}/${trimLeadingSlash(version)}`;
  return `${trimTrailingSlash(composed)}/`;
};

const baseURL = resolveBaseURL();

export const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 調試信息（僅開發環境）
if (process.env.NODE_ENV === 'development') {
  console.log('🔗 API Configuration:', {
    baseURL,
    environment: process.env.REACT_APP_ENV,
  });
}

// 請求攔截器 - 自動添加 Authorization header
api.interceptors.request.use(
  (config) => {
    if (config.baseURL && typeof config.url === 'string' && config.url.startsWith('/')) {
      config.url = trimLeadingSlash(config.url);
    }

    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 調試信息（僅開發環境）
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 API Request:', {
        url: `${config.baseURL}${config.url}`,
        method: config.method,
        hasAuth: !!token,
      });
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// 響應攔截器 - 處理通用錯誤
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📥 API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Response Error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
    
    // 處理 401 - 未授權
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      // 可以在這裡觸發登出邏輯
    }
    
    return Promise.reject(error);
  }
);

/**
 * 解析錯誤訊息 - 統一錯誤處理
 * @param {Error} error - axios 錯誤對象
 * @returns {string} 格式化的錯誤訊息
 */
export const parseErrorMessage = (error) => {
  if (error.response) {
    // 服務器回應錯誤
    const { data, status } = error.response;
    
    if (data && typeof data === 'object') {
      // 處理常見的 Django REST framework 錯誤格式
      if (data.detail) {
        return data.detail;
      }
      
      // 處理字段驗證錯誤
      if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
        return data.non_field_errors.join(', ');
      }
      
      // 處理其他字段錯誤
      const fieldErrors = [];
      Object.keys(data).forEach(field => {
        if (Array.isArray(data[field])) {
          fieldErrors.push(`${field}: ${data[field].join(', ')}`);
        } else if (typeof data[field] === 'string') {
          fieldErrors.push(`${field}: ${data[field]}`);
        }
      });
      
      if (fieldErrors.length > 0) {
        return fieldErrors.join('; ');
      }
    }
    
    // 根據狀態碼返回通用錯誤訊息
    switch (status) {
      case 400:
        return '請求參數錯誤';
      case 401:
        return '認證失敗，請重新登入';
      case 403:
        return '沒有權限執行此操作';
      case 404:
        return '請求的資源不存在';
      case 500:
        return '伺服器內部錯誤';
      default:
        return `請求失敗 (${status})`;
    }
  } else if (error.request) {
    // 網路錯誤
    return '網路連線失敗，請檢查網路狀態';
  } else {
    // 其他錯誤
    return error.message || '未知錯誤';
  }
};

// API 端點函數
export const getTopics = () => api.get('topics/');
export const getAds = () => api.get('ads/');
export const getHotPosts = (limit = 10) => api.get('posts/hot/', { params: { limit } });

// 認證相關
export const login = (credentials) => api.post('token/', credentials);
export const refreshToken = (token) => api.post('token/refresh/', { refresh: token });

// 默認導出
export default api;
